import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { ComingSoonPage } from '@/components/ComingSoonPage';
import { AuthProvider } from '@/lib/auth';
import { NavHistoryProvider } from '@/lib/navHistory';
import { RequireAuth } from '@/components/RequireAuth';
import { LoginPage } from '@/features/auth/LoginPage';
import { SignupPage } from '@/features/auth/SignupPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';

import { HomePage } from '@/features/home/HomePage';
import { WardrobeHubPage } from '@/features/wardrobe/WardrobeHubPage';
import { WardrobeItemsPage } from '@/features/wardrobe/WardrobeItemsPage';
import { WardrobeAddPage } from '@/features/wardrobe/WardrobeAddPage';
import { ItemDetailPage } from '@/features/wardrobe/ItemDetailPage';
import { ItemWearsPage } from '@/features/wardrobe/ItemWearsPage';
import { WardrobeTailorPage } from '@/features/wardrobe/WardrobeTailorPage';

import { FitsHubPage } from '@/features/fits/FitsHubPage';
import { FitsAvailablePage } from '@/features/fits/FitsAvailablePage';
import { FitBuilderPage } from '@/features/fits/FitBuilderPage';
import { FitBuilderSessionsPage } from '@/features/fits/FitBuilderSessionsPage';
import { WhatToWearPage } from '@/features/fits/WhatToWearPage';
import { FitAddPage } from '@/features/fits/FitAddPage';
import { FitTimelinePage } from '@/features/fits/FitTimelinePage';
import { FitDetailPage } from '@/features/fits/FitDetailPage';
import { FitVersionPage } from '@/features/fits/FitVersionPage';
import { FitWearPage } from '@/features/fits/FitWearPage';

import { PlannerPage } from '@/features/planner/PlannerPage';
import { PlannerNewPage } from '@/features/planner/PlannerNewPage';
import { PlannerDetailPage } from '@/features/planner/PlannerDetailPage';

import { BeforeBuyHubPage } from '@/features/before-buy/BeforeBuyHubPage';
import { GapReportPage } from '@/features/before-buy/GapReportPage';
import { AiSuggestionsPage } from '@/features/before-buy/AiSuggestionsPage';

import { CameraPage } from '@/features/camera/CameraPage';
import { CameraRollImportPage } from '@/features/intake/CameraRollImportPage';
import { SearchPage } from '@/features/search/SearchPage';

import { ProfilePage } from '@/features/profile/ProfilePage';
import { AccountPage } from '@/features/profile/AccountPage';
import { ClosetsPage } from '@/features/profile/ClosetsPage';
import { ClosetDetailPage } from '@/features/profile/ClosetDetailPage';
import { SizesPage } from '@/features/profile/SizesPage';
import { StoresPage } from '@/features/profile/StoresPage';
import { DesignersPage } from '@/features/profile/DesignersPage';
import { StyleProfilePage } from '@/features/profile/StyleProfilePage';
import { ServicesPage } from '@/features/profile/ServicesPage';
import { WishlistArchivePage } from '@/features/profile/WishlistArchivePage';

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NavHistoryProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          <Route element={<RequireAuth />}>
            <Route element={<AppShell />}>
              <Route index element={<HomePage />} />

              {/* Wardrobe */}
              <Route path="wardrobe" element={<WardrobeHubPage />} />
              <Route path="wardrobe/items" element={<WardrobeItemsPage />} />
              <Route path="wardrobe/add" element={<WardrobeAddPage />} />
              <Route path="wardrobe/item/:id" element={<ItemDetailPage />} />
              <Route path="wardrobe/item/:id/wears" element={<ItemWearsPage />} />
              <Route path="wardrobe/import" element={<CameraRollImportPage />} />
              <Route path="wardrobe/tailor" element={<WardrobeTailorPage />} />

              {/* Fits */}
              <Route path="fits" element={<FitsHubPage />} />
              <Route path="fits/today" element={<WhatToWearPage />} />
              <Route path="fits/available" element={<FitsAvailablePage />} />
              <Route path="fits/builder/sessions" element={<FitBuilderSessionsPage />} />
              <Route path="fits/builder" element={<FitBuilderPage />} />
              <Route path="fits/add" element={<FitAddPage />} />
              <Route path="fits/timeline" element={<FitTimelinePage />} />
              <Route path="fits/planner" element={<PlannerPage />} />
              <Route path="fits/planner/new" element={<PlannerNewPage />} />
              <Route path="fits/planner/:id" element={<PlannerDetailPage />} />
              <Route path="fits/:id" element={<FitDetailPage />} />
              <Route path="fits/:id/version/:version" element={<FitVersionPage />} />
              <Route path="fits/:id/wear/:wearId" element={<FitWearPage />} />

              {/* Before You Buy */}
              <Route path="before-buy" element={<BeforeBuyHubPage />} />
              <Route path="before-buy/ai-suggestions" element={<AiSuggestionsPage />} />
              <Route path="before-buy/gap-report" element={<GapReportPage />} />

              {/* Camera + Search */}
              <Route path="camera" element={<CameraPage />} />
              <Route path="search" element={<SearchPage />} />

              {/* Profile */}
              <Route path="profile" element={<ProfilePage />} />
              <Route path="profile/account" element={<AccountPage />} />
              <Route path="profile/closets" element={<ClosetsPage />} />
              <Route path="profile/closets/:id" element={<ClosetDetailPage />} />
              <Route path="profile/sizes" element={<SizesPage />} />
              <Route path="profile/stores" element={<StoresPage />} />
              <Route path="profile/designers" element={<DesignersPage />} />
              <Route path="profile/style" element={<StyleProfilePage />} />
              <Route path="profile/services" element={<ServicesPage />} />
              <Route path="profile/archive/wishlist" element={<WishlistArchivePage />} />
              <Route path="profile/:section" element={<ComingSoonPage />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
        </NavHistoryProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
